/*
 * Convert markdown source to readable plain text for the clipboard.
 *
 * Used for the text/plain half of a copy. The text/html half comes from the
 * rendered DOM, which is the faithful representation of what the user is
 * looking at — but a plain-text target needs the syntax removed rather than
 * the formatting reproduced.
 *
 * Deliberately works from the markdown SOURCE rather than the rendered node's
 * innerText. List markers are literal in the source ("- ", "1. ") but are drawn
 * by CSS ::before pseudo-elements in the rendered output, so innerText would
 * silently drop every bullet and number. Pasting a stripped bullet list into an
 * email is worse than pasting one with its dashes intact.
 *
 * No lookbehind assertions: they are a runtime syntax error on Safari before
 * 16.4 and Babel cannot transpile them, so preceding characters are captured
 * and re-emitted instead.
 */

export function markdownToPlainText(md) {
  if (md == null) return '';
  let s = String(md).replace(/\r\n?/g, '\n');

  // Lift fenced code blocks out so their contents are not de-formatted.
  // U+E000 is a private-use character: it cannot appear in real prose, and
  // unlike NUL it is not a control character, so it stays lint-clean.
  const FENCE = '\uE000';
  const fences = [];
  s = s.replace(/```[^\n]*\n?([\s\S]*?)```/g, (_, code) => {
    fences.push(String(code).replace(/\n$/, ''));
    return `${FENCE}${fences.length - 1}${FENCE}`;
  });

  s = s
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')          // images -> alt text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')            // links -> label
    .replace(/^ {0,3}(#{1,6})\s+/gm, '')                // ATX headings
    .replace(/^\s*={2,}\s*$/gm, '')                     // setext underline
    .replace(/^ {0,3}>\s?/gm, '')                       // blockquote markers
    .replace(/^ {0,3}(?:\*\s*){3,}$/gm, '')             // thematic break ***
    .replace(/^ {0,3}(?:-\s*){3,}$/gm, '')              // thematic break ---
    .replace(/^ {0,3}(?:_\s*){3,}$/gm, '')              // thematic break ___
    .replace(/`([^`\n]+)`/g, '$1');                     // inline code

  // Emphasis. Longest markers first so ** is consumed before *.
  s = s
    .replace(/\*\*\*(?=\S)([\s\S]*?\S)\*\*\*/g, '$1')
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '$1')
    .replace(/\*(?=\S)([^*\n]*?\S)\*/g, '$1')
    .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1');

  // Underscore emphasis only at word boundaries, so snake_case survives.
  s = s
    .replace(/(^|[^\w\\])___(?=\S)([^\n]*?\S)___(?!\w)/g, '$1$2')
    .replace(/(^|[^\w\\])__(?=\S)([^\n]*?\S)__(?!\w)/g, '$1$2')
    .replace(/(^|[^\w\\])_(?=\S)([^_\n]*?\S)_(?!\w)/g, '$1$2');

  // GFM table separator rows carry no meaning once the pipes are plain text.
  // Requires two pipes and only pipe/dash/colon/space between them, so a data
  // row containing words is never matched. The trailing newline goes with it,
  // otherwise the blank line left behind splits the table into two paragraphs.
  s = s.replace(/^[ \t]*\|[ \t:|-]*\|[ \t]*\n?/gm, (line) => (line.includes('-') ? '' : line));

  // Double backslash: this is a string, so '\d' would collapse to a literal 'd'.
  s = s.replace(new RegExp(FENCE + '(\\d+)' + FENCE, 'g'), (_, i) => fences[Number(i)] ?? '');

  return s
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default markdownToPlainText;
